import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Header from '../../components/organisms/Header/Header';
import Container from '../../components/atoms/Container/Container';
import Button from '../../components/atoms/Button/Button';
import { ButtonGroup } from '../../components/ButtonGroup/ButtonGroup';
import useTheme from '../../hooks/useTheme';
import { Transaction } from '../../services/types';
import { getCurrencySymbol } from '../../services/utils';

const secondsLeft = (expireAt?: string): number | null => {
  if (!expireAt) return null;
  const ms = new Date(expireAt).getTime() - Date.now();
  return ms > 0 ? Math.floor(ms / 1000) : 0;
};
const mmss = (s: number) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const money = (n: number, cur?: string) =>
  `${n.toLocaleString('ru-RU')}${getCurrencySymbol(cur) ?? (cur ? ` ${cur}` : '')}`;

const ACCENT = '#567BFF';

// Centering wrapper — the shared Container centers only inside a flex column
// (other Default screens sit inside DefaultTemplate's WrapperDiv).
const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

// Content sits inside the shared Container backing (подложка) like every other Default page.
const Inner = styled.div<{ $dark: boolean }>`
  text-align: center;
  color: ${({ $dark }) => ($dark ? '#C7CEE6' : '#3C4670')};
  font-family: 'PT Sans', sans-serif;
  padding: 6px 4px 10px;
`;

const Title = styled.div`
  font-size: 19px;
  font-weight: 700;
  margin: 4px 0 8px;
`;

const Intro = styled.div<{ $dark: boolean }>`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ $dark }) => ($dark ? '#828BAD' : '#6E7CAF')};
  margin-bottom: 16px;
`;

const Timer = styled.div<{ $dark: boolean }>`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  border-radius: 999px;
  padding: 6px 16px;
  margin-bottom: 20px;
  background: ${({ $dark }) => ($dark ? '#141722' : '#ECF4FF')};
  border: 1px solid ${ACCENT}55;
  & b { color: ${ACCENT}; font-variant-numeric: tabular-nums; letter-spacing: 1px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const Tile = styled.button<{ $selected: boolean; $dark: boolean }>`
  padding: 16px 8px;
  border-radius: 14px;
  cursor: pointer;
  font-family: 'PT Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  transition: transform 0.12s, box-shadow 0.2s, background 0.15s, border-color 0.15s;
  color: ${({ $selected, $dark }) => ($selected ? '#fff' : $dark ? '#C7CEE6' : '#3C4670')};
  background: ${({ $selected, $dark }) => ($selected ? ACCENT : $dark ? '#141722' : '#FFFFFF')};
  border: 1.5px solid ${({ $selected, $dark }) => ($selected ? ACCENT : $dark ? '#242a3d' : '#D7E0FF')};
  box-shadow: ${({ $selected }) => ($selected ? `0 6px 16px ${ACCENT}44` : '0 2px 8px rgba(80,110,220,0.06)')};
  &:hover:not(:disabled) { transform: translateY(-2px); border-color: ${ACCENT}; }
  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { cursor: default; opacity: 0.6; }
`;

// CPAYMENT-1670: the "search a new requisite" action sits under the main pair, not inside it —
// three buttons in one row do not fit the mobile layout.
const RetryRow = styled.div`
  margin-top: 12px;
`;

interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onCancel: () => void;
  onSelectVariant?: (variantId: number) => void;
  variantSubmitting?: boolean;
  onRetrySearch?: () => Promise<void> | void;
  retrySearching?: boolean;
}

const VariantPage = ({
  transaction,
  uuid,
  timeLeft,
  onCancel,
  onSelectVariant,
  variantSubmitting,
  onRetrySearch,
  retrySearching,
}: Props) => {
  const theme = useTheme();
  const dark = theme === 'dark';
  const tx = transaction as any;
  const variants: { id: number; amount: number }[] = tx.variants ?? [];
  const currency = tx.currency ?? tx.requisite?.currency ?? 'RUB';

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [left, setLeft] = useState<number | null>(secondsLeft(tx.variantsExpireAt));

  useEffect(() => {
    if (!tx.variantsExpireAt) return;
    const id = setInterval(() => setLeft(secondsLeft(tx.variantsExpireAt)), 1000);
    return () => clearInterval(id);
  }, [tx.variantsExpireAt]);

  return (
    <Page>
      <Header transaction={transaction} ticketId={uuid} timeLeft={timeLeft} />
      <Container>
        <Inner $dark={dark}>
        <Title>Выберите сумму пополнения</Title>
        <Intro $dark={dark}>
          Пока не удалось подобрать реквизит под сумму {money(tx.amount, currency)}. Ниже есть
          реквизиты под другие суммы — выберите подходящую, и мы зачислим именно её.
        </Intro>

        {left !== null && (
          <Timer $dark={dark}>
            Осталось выбрать: <b>{mmss(left)}</b>
          </Timer>
        )}

        <Grid>
          {variants.map((v) => (
            <Tile
              key={v.id}
              type='button'
              $selected={selectedId === v.id}
              $dark={dark}
              disabled={variantSubmitting}
              onClick={() => setSelectedId(v.id)}
              data-sentry-element='variant-tile'
            >
              {money(v.amount, currency)}
            </Tile>
          ))}
        </Grid>

        <ButtonGroup>
          <Button
            variant='secondary'
            onClick={onCancel}
            disabled={variantSubmitting}
            dataSentryElement='variant-back'
          >
            Отмена
          </Button>
          <Button
            variant='primary'
            disabled={selectedId === null || variantSubmitting}
            onClick={() => selectedId !== null && onSelectVariant?.(selectedId)}
            dataSentryElement='variant-pay'
          >
            Пополнить
          </Button>
        </ButtonGroup>

        {tx.canRetryRequisiteSearch && onRetrySearch && (
          <RetryRow>
            <Button
              variant='secondary'
              outlined
              width='100%'
              disabled={variantSubmitting || retrySearching}
              onClick={onRetrySearch}
              dataSentryElement='retry-search'
            >
              Найти новый реквизит
            </Button>
          </RetryRow>
        )}
        </Inner>
      </Container>
    </Page>
  );
};

export default VariantPage;
